"use client";

// ============================================================
// MEMORY CORE — 기억 우주 (레퍼런스: 뇌세포 우주 / 터미널 HUD 스타일)
// - 나의 세계에서 뮤즈를 탭하면 진입하는 풀스크린 화면
// - 기억 하나 = 빛나는 입자. 같은 종류끼리 성단(星團)처럼 뭉쳐 무지개 구름을 이룬다
// - 중심의 큰 백색 항성 = 호칭(뮤즈가 나를 부르는 이름)
// - HUD: MEMORY CORE / NEURAL LINK ACTIVE / [ LINKS : ON ] [ TOUR ] — 모노스페이스 터미널 감성
// - 개념: "코어 기억" 하나 — 추억을 쌓을수록 뮤즈가 나를 더 많이 기억하고,
//        친밀도가 오를수록 기억할 수 있는 코어가 늘어난다
// ============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { formatDiamond } from '@/utils/formatDiamond';
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/icons";
import { useTranslator } from "@/lang";
import { usePortalStore } from "@/stores/portalStore";
import { useDiamondShortageModal } from "@/hooks/useDiamondShortageModal";
import { usePaidDiamondModal } from "@/hooks/usePaidDiamondModal";
import { useRelationships, RelationshipDetailSheetBody } from "@/app/chat/features/RelationshipGalaxy";
import {
  getMemoryUniverse,
  addMuseMemory,
  promoteMuseMemory,
  updateMuseMemory,
  deleteMuseMemory,
  setMuseCallName,
  type MuseMemoryItem,
  type MuseMemoryUniverse as UniverseData,
} from "@/api/museMemoryApi";
import { getCharacterBasicInfo, setChatRoomBackground } from "@/api/feedApi";
import { deleteMuseAsset, getMuseAssetList, type MuseAssetItem } from "@/api/museAssetApi";
import { getAssetUrl } from "@/utils/assetUrl";
import { getLocalizedText } from "@/utils/getLocalizedText";
import { resolveIntimacyLabel } from "@/utils/intimacyBranding";
import { markUniverseVisited } from "@/utils/museUniverseBadge";
import { TEMP_FG_DIAMOND } from "@/constants/tempColors";
import { TEMP_TEXT_BOLD_M, TEMP_TEXT_SEMIBOLD_M, TEMP_TEXT_SEMIBOLD_XS, TEMP_TEXT_BOLD_XS } from "@/constants/tempTypo";

// ── HUD 팔레트 (터미널) ──
const HUD = {
  cyan: "#6FE3FF",
  teal: "#3EE6C0",
  green: "#7CFF9E",
  red: "#FF4D5E",
  amber: "#FFC94A",
  text: "#D9EAF6",
  dim: "rgba(150,190,220,0.55)",
  panelBg: "rgba(5,11,24,0.86)",
  panelBorder: "rgba(110,200,255,0.28)",
} as const;

const MONO = "'Menlo','Roboto Mono','Courier New',monospace";

// 크리에이터(공식 뮤즈)가 팬 자율 설정을 허용하지 않았을 때의 안내 문구 (i18n 키 — 사용처에서 t() 적용)
const LOCKED_NOTICE_KEY = "universe.lockedNotice";

// HUD 패널 공통
const PANEL = {
  bgcolor: HUD.panelBg,
  border: `1px solid ${HUD.panelBorder}`,
  borderRadius: "6px",
  backdropFilter: "blur(10px)",
} as const;

// ── 성단(클러스터) 정의 — 기억 종류별 색 계열 (무지개 구름) ──
// 같은 클러스터 안에서도 입자마다 3단계 색조 변화를 줘 살아있는 구름처럼 보이게 한다.
const CLUSTERS = {
  CORE: { hues: ["#FFE9A8", "#FFD86B", "#FFB454"], label: "CORE" }, // 골드 — 코어 기억
  STABLE: { hues: ["#9BD4FF", "#5FA8FF", "#7B8FFF"], label: "MEM" }, // 블루 — 오래 남는 기억
  CURRENT: { hues: ["#A8FFD9", "#52E8B8", "#7CFF9E"], label: "NOW" }, // 그린 — 최근 기억
  PROMISE: { hues: ["#E3C8FF", "#B08CFF", "#8F6BFF"], label: "PRM" }, // 바이올렛 — 약속
  FADED: { hues: ["#6A7488", "#525C6E", "#3E4757"], label: "FADE" }, // 슬레이트 — 흐려진 기억
} as const;
type ClusterKey = keyof typeof CLUSTERS;

const RECENT_RECALL_MS = 72 * 60 * 60 * 1000;
const GOLDEN_ANGLE = 137.50776;

function isRecentlyRecalled(m?: MuseMemoryItem): boolean {
  if (!m?.lastRecalledAt) return false;
  return Date.now() - new Date(m.lastRecalledAt).getTime() < RECENT_RECALL_MS;
}

function isCore(m: MuseMemoryItem): boolean {
  return m.tier !== "MEMORY"; // CORE + 레거시 KEEPSAKE
}

// 결정적 의사난수 (SSR/리렌더 안정)
function prand(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

type UniverseNode = {
  key: string;
  kind: "memory" | "promise";
  memory?: MuseMemoryItem;
  promiseContent?: string;
  color: string;
  x: number;
  y: number;
  size: number;
  dim: boolean;
  recalledRecently: boolean;
};

type DustParticle = { x: number; y: number; s: number; color: string; o: number; d: number };

// ── 성단 배치 — 클러스터 중심 주변에 입자를 흩뿌려 구름을 만든다 ──
function buildUniverseLayout(data: UniverseData): {
  nodes: UniverseNode[];
  dust: DustParticle[];
  canvas: number;
  center: number;
} {
  const core = data.memories.filter((m) => isCore(m) && m.status === "ACTIVE");
  const stable = data.memories.filter((m) => m.tier === "MEMORY" && m.status === "ACTIVE" && m.bucket !== "CURRENT");
  const current = data.memories.filter((m) => m.tier === "MEMORY" && m.status === "ACTIVE" && m.bucket === "CURRENT");
  const faded = data.memories.filter((m) => m.status === "FADED");

  const total = core.length + stable.length + current.length + faded.length + data.promises.length;
  const canvas = Math.round(Math.max(820, 620 + total * 12));
  const center = canvas / 2;

  const nodes: UniverseNode[] = [];
  const dust: DustParticle[] = [];
  let seed = 1;

  // 클러스터 중심: 중심 항성 주위로 방위각을 나눠 배치
  const clusterCenter = (angleDeg: number, dist: number) => ({
    cx: center + dist * Math.cos((angleDeg * Math.PI) / 180),
    cy: center + dist * Math.sin((angleDeg * Math.PI) / 180),
  });

  const placeCluster = (
    items: { memory?: MuseMemoryItem; promiseContent?: string; kind: "memory" | "promise"; key: string }[],
    clusterKey: ClusterKey,
    angleDeg: number,
    dist: number,
    spread: number,
    baseSize: number,
    dimmed: boolean,
  ) => {
    const { cx, cy } = clusterCenter(angleDeg, dist);
    const hues = CLUSTERS[clusterKey].hues;
    items.forEach((item, i) => {
      seed += 1;
      // 황금각 나선 + 지터로 구름 형태
      const r = spread * (0.25 + 0.75 * Math.sqrt((i + 1) / Math.max(4, items.length)));
      const a = (i * GOLDEN_ANGLE + seed * 37) * (Math.PI / 180);
      const jx = (prand(seed * 3.7) - 0.5) * spread * 0.5;
      const jy = (prand(seed * 7.3) - 0.5) * spread * 0.5;
      const x = cx + r * Math.cos(a) + jx;
      const y = cy + r * Math.sin(a) + jy;
      const color = hues[i % hues.length];
      const recallBoost = item.memory ? Math.min(6, (item.memory.recallCount ?? 0) * 1.3) : 0;
      const size = baseSize + recallBoost + prand(seed * 1.9) * 3;

      nodes.push({
        key: item.key,
        kind: item.kind,
        memory: item.memory,
        promiseContent: item.promiseContent,
        color,
        x,
        y,
        size,
        dim: dimmed,
        recalledRecently: isRecentlyRecalled(item.memory),
      });

      // 주변 먼지 입자 4~5개 — 성단 구름의 볼륨감
      const dustCount = dimmed ? 2 : 4 + Math.round(prand(seed * 5.1));
      for (let k = 0; k < dustCount; k++) {
        const da = prand(seed * 11 + k) * Math.PI * 2;
        const dr = 8 + prand(seed * 13 + k) * 26;
        dust.push({
          x: x + dr * Math.cos(da),
          y: y + dr * Math.sin(da),
          s: 1.5 + prand(seed * 17 + k) * 2.5,
          color: hues[(i + k) % hues.length],
          o: dimmed ? 0.12 : 0.2 + prand(seed * 19 + k) * 0.3,
          d: prand(seed * 23 + k) * 2.5,
        });
      }
    });
  };

  placeCluster(core.map((m) => ({ memory: m, kind: "memory" as const, key: m.id })), "CORE", -30, 105, 62, 13, false);
  placeCluster(stable.map((m) => ({ memory: m, kind: "memory" as const, key: m.id })), "STABLE", 150, 185, 95, 9, false);
  placeCluster(current.map((m) => ({ memory: m, kind: "memory" as const, key: m.id })), "CURRENT", 55, 195, 85, 9, false);
  placeCluster(
    data.promises.map((p) => ({ promiseContent: p.content, kind: "promise" as const, key: `promise-${p.id}` })),
    "PROMISE",
    255,
    200,
    70,
    10,
    false,
  );
  placeCluster(faded.map((m) => ({ memory: m, kind: "memory" as const, key: m.id })), "FADED", 205, 300, 110, 6, true);

  return { nodes, dust, canvas, center };
}

// ── 연관 기억 연결(LINKS) — 공유 키워드 기반 ──
const LINK_STOPWORDS = new Set(["유저", "그리고", "하지만", "그래서", "요즘", "오늘", "최근", "함께", "서로", "매우", "아주", "자주", "좋아함", "싫어함"]);

function linkKeywords(content: string): string[] {
  return Array.from(new Set(content.split(/[^0-9A-Za-z가-힣]+/).filter((w) => w.length >= 2 && !LINK_STOPWORDS.has(w))));
}

type UniverseLink = { key: string; x1: number; y1: number; x2: number; y2: number };

function computeLinks(nodes: UniverseNode[]): UniverseLink[] {
  const memoryNodes = nodes.filter((n) => n.kind === "memory" && n.memory && !n.dim);
  const kws = memoryNodes.map((n) => linkKeywords(n.memory!.content));
  const links: UniverseLink[] = [];
  const degree = new Map<string, number>();

  for (let i = 0; i < memoryNodes.length && links.length < 60; i++) {
    for (let j = i + 1; j < memoryNodes.length && links.length < 60; j++) {
      if ((degree.get(memoryNodes[i].key) ?? 0) >= 3) break;
      if ((degree.get(memoryNodes[j].key) ?? 0) >= 3) continue;
      let score = 0;
      for (const kw of kws[i]) {
        if (kws[j].includes(kw)) score += kw.length >= 3 ? 1 : 0.5;
      }
      if (score >= 1) {
        links.push({
          key: `${memoryNodes[i].key}-${memoryNodes[j].key}`,
          x1: memoryNodes[i].x,
          y1: memoryNodes[i].y,
          x2: memoryNodes[j].x,
          y2: memoryNodes[j].y,
        });
        degree.set(memoryNodes[i].key, (degree.get(memoryNodes[i].key) ?? 0) + 1);
        degree.set(memoryNodes[j].key, (degree.get(memoryNodes[j].key) ?? 0) + 1);
      }
    }
  }
  return links;
}

// ── 배경 별 (파라락스 2겹) ──
const STARS_FAR = [
  { top: "4%", left: "12%" }, { top: "9%", left: "68%" }, { top: "15%", left: "38%" }, { top: "21%", left: "84%" },
  { top: "29%", left: "6%" }, { top: "37%", left: "52%" }, { top: "45%", left: "91%" }, { top: "53%", left: "22%" },
  { top: "61%", left: "66%" }, { top: "69%", left: "44%" }, { top: "77%", left: "88%" }, { top: "83%", left: "16%" },
  { top: "91%", left: "58%" }, { top: "12%", left: "94%" }, { top: "58%", left: "4%" }, { top: "95%", left: "36%" },
];
const STARS_NEAR = [
  { top: "7%", left: "26%" }, { top: "17%", left: "72%" }, { top: "31%", left: "12%" }, { top: "43%", left: "82%" },
  { top: "55%", left: "48%" }, { top: "67%", left: "8%" }, { top: "74%", left: "64%" }, { top: "86%", left: "30%" },
  { top: "93%", left: "78%" }, { top: "25%", left: "50%" },
];

type Gesture =
  | { type: "pinch"; startDist: number; startScale: number; px: number; py: number; cx: number; cy: number }
  | { type: "pan"; startX: number; startY: number; startTx: number; startTy: number };

function formatRelative(
  iso: string,
  t: (key: string) => string,
  f: (template: string, args: (string | number)[]) => string,
): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return t("universe.time.today");
  if (days === 1) return t("universe.time.yesterday");
  if (days < 30) return f(t("my_feed.time.days_ago"), [days]);
  return f(t("universe.time.monthsAgo"), [Math.floor(days / 30)]);
}

// 기억 원문은 "유저"가 주어지만, 보는 사람 입장에선 "나" — 표시할 때만 1인칭으로 변환.
// LLM이 가끔 유저를 2인칭(너/당신)으로 적는 경우도 함께 흡수한다 ("너의 눈동자" → "나의 눈동자").
// ※ 조사가 붙은 형태만 치환하므로 "너무" 같은 단어는 건드리지 않는다.
function toFirstPerson(content: string): string {
  return content
    .replace(/유저는/g, "나는")
    .replace(/유저가/g, "내가")
    .replace(/유저를/g, "나를")
    .replace(/유저와/g, "나와")
    .replace(/유저의/g, "나의")
    .replace(/유저에게/g, "나에게")
    .replace(/유저한테/g, "나한테")
    .replace(/유저도/g, "나도")
    .replace(/유저/g, "나")
    .replace(/너의/g, "나의")
    .replace(/너를/g, "나를")
    .replace(/너와/g, "나와")
    .replace(/너랑/g, "나랑")
    .replace(/너는/g, "나는")
    .replace(/너도/g, "나도")
    .replace(/너에게/g, "나에게")
    .replace(/너한테/g, "나한테")
    .replace(/네가/g, "내가")
    .replace(/너가/g, "내가")
    .replace(/당신의/g, "나의")
    .replace(/당신을/g, "나를")
    .replace(/당신은/g, "나는")
    .replace(/당신이/g, "내가")
    .replace(/당신과/g, "나와");
}

// 관계 단계 색 (나의 세계 getStage와 동일 체계)
function stageColorOf(intimacy: number, isGirlfriend: boolean): string {
  if (isGirlfriend) return "#FE375B";
  if (intimacy <= 20) return "#9e9e9e";
  if (intimacy <= 40) return "#FFB07A";
  if (intimacy <= 70) return "#FF6B8A";
  if (intimacy <= 90) return "#F75555";
  return "#FE375B";
}

export function MuseUniverse({ characterId, onBackPress }: { characterId: string; onBackPress: () => void }) {
  const { t, f, language } = useTranslator();
  const qc = useQueryClient();
  const openBottomSheet = usePortalStore((s) => s.openBottomSheet);
  const closeById = usePortalStore((s) => s.closeById);
  const { showDiamondShortage, isInsufficientBalanceError } = useDiamondShortageModal();
  const { showPaidDiamondRequired } = usePaidDiamondModal();

  const { data, isLoading } = useQuery({
    queryKey: ["museMemory", characterId],
    queryFn: () => getMemoryUniverse(characterId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // 기억 생성중이면 4초 폴링 — 별이 태어나는 순간을 놓치지 않는다
    refetchInterval: (query) => (query.state.data?.isGenerating ? 4000 : false),
  });

  // 친밀도 브랜딩 — 공식 뮤즈 커스텀 표시 이름 (1:1 채팅과 같은 캐시 키라 대부분 즉시 히트)
  const { data: characterProfile } = useQuery({
    queryKey: ["character", characterId],
    queryFn: () => getCharacterBasicInfo(characterId),
    enabled: !!characterId,
  });
  const intimacyBrandLabel =
    characterProfile?.isRealPerson === true ? resolveIntimacyLabel(characterProfile) : null;

  const { data: relData } = useRelationships();
  const relItem = relData?.items.find((i) => i.characterId === characterId) ?? null;

  // 방문 처리 — 나의 세계/채팅 리스트의 NEW 배지 해제
  // 기준점으로 "지금 보고 있는 활성 별 개수"를 저장 → 다음 NEW는 실제로 별이 더 생겨야 켜진다.
  // 우주 데이터 로드 전엔 이전 기준 유지(markUniverseVisited가 undefined는 무시), 로드/폴링으로
  // 별이 늘어나면 기준점도 따라 갱신(보고 있는 별은 이미 본 것).
  const visitedActiveCount = data ? data.memories.filter((m) => m.status === "ACTIVE").length : null;
  useEffect(() => {
    markUniverseVisited(characterId, {
      chatCount: relItem?.chatCount ?? null,
      memoryCount: visitedActiveCount,
    });
  }, [characterId, relItem?.chatCount, visitedActiveCount]);
  const museName = relItem?.name ? getLocalizedText(relItem.name, language) : "";
  const museAvatar = relItem?.profileImagePath ? getAssetUrl(relItem.profileImagePath, "large") : "";

  // 마음(하트) — 관계 단계에 따라 색이 변하고, 새 속마음이 오면 빨간 점 알럿
  // 알럿 남발 방지: 확인 후 6시간 쿨다운 (그 안에 속마음이 또 바뀌어도 재점등 안 함)
  const HEART_ALERT_COOLDOWN_MS = 6 * 60 * 60 * 1000;
  const heartColor = relItem ? stageColorOf(relItem.intimacy, relItem.isGirlfriend) : "#FF7D96";
  const heartSeenKey = `museHeartSeen:${characterId}`;
  const [hasNewImpression, setHasNewImpression] = useState(false);
  useEffect(() => {
    if (!relItem?.impression) {
      setHasNewImpression(false);
      return;
    }
    try {
      const raw = localStorage.getItem(heartSeenKey);
      let seen: { impression: string; seenAt: number } | null = null;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.impression === "string") seen = parsed;
        } catch {
          seen = { impression: raw, seenAt: 0 }; // 구버전(문자열) 호환
        }
      }
      if (!seen) {
        setHasNewImpression(true);
        return;
      }
      const changed = seen.impression !== relItem.impression;
      const cooledDown = Date.now() - seen.seenAt > HEART_ALERT_COOLDOWN_MS;
      setHasNewImpression(changed && cooledDown);
    } catch {
      setHasNewImpression(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relItem?.impression, heartSeenKey]);

  // 레이아웃은 "내용이 실제로 바뀔 때만" 재계산 — 폴링마다 새 객체가 와도
  // 노드가 재배치/재마운트되지 않게 시그니처로 고정 (불필요한 리렌더 방지)
  const layoutSig = data
    ? `${data.memories.map((m) => `${m.id}:${m.tier}:${m.status}:${m.recallCount}`).join("|")}#${data.promises.map((p) => p.id).join(",")}`
    : "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const layout = useMemo(() => (data ? buildUniverseLayout(data) : null), [layoutSig]);

  const [selected, setSelected] = useState<UniverseNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // LINKS
  const [linksOn, setLinksOn] = useState(true);
  const links = useMemo(() => (layout && linksOn ? computeLinks(layout.nodes) : []), [layout, linksOn]);

  // 새 별 발견 (기기 로컬)
  const seenKey = `museUniverseSeen:${characterId}`;
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    try {
      setSeenIds(new Set(JSON.parse(localStorage.getItem(seenKey) ?? "[]") as string[]));
    } catch {
      setSeenIds(new Set());
    }
  }, [seenKey]);
  const markSeen = (id: string) => {
    setSeenIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try {
        localStorage.setItem(seenKey, JSON.stringify(Array.from(next)));
      } catch {
        // storage 불가 환경 무시
      }
      return next;
    });
  };

  // TOUR — 시간순 자동 크루즈
  const [tourIdx, setTourIdx] = useState<number | null>(null);
  const tourNodes = useMemo(
    () =>
      layout
        ? layout.nodes
            .filter((n) => n.kind === "memory" && n.memory)
            .sort((a, b) => new Date(a.memory!.createdAt).getTime() - new Date(b.memory!.createdAt).getTime())
        : [],
    [layout],
  );

  // ── 제스처/파라락스 ──
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canvasElRef = useRef<HTMLDivElement | null>(null);
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const gestureRef = useRef<Gesture | null>(null);
  const draggedRef = useRef(false);
  const bgFarRef = useRef<HTMLDivElement | null>(null);
  const bgNearRef = useRef<HTMLDivElement | null>(null);

  const applyParallax = (tx: number, ty: number, smooth: boolean) => {
    const transition = smooth ? "transform 0.9s cubic-bezier(.22,.9,.32,1)" : "";
    if (bgFarRef.current) {
      bgFarRef.current.style.transition = transition;
      bgFarRef.current.style.transform = `translate(${tx * 0.1}px, ${ty * 0.1}px)`;
    }
    if (bgNearRef.current) {
      bgNearRef.current.style.transition = transition;
      bgNearRef.current.style.transform = `translate(${tx * 0.26}px, ${ty * 0.26}px)`;
    }
  };
  const applyParallaxRef = useRef(applyParallax);
  applyParallaxRef.current = applyParallax;

  const stopTourRef = useRef<() => void>(() => {});
  stopTourRef.current = () => {
    if (canvasElRef.current) canvasElRef.current.style.transition = "";
    setTourIdx(null);
  };

  // focusY: 노드를 화면 세로 어디에 둘지 (0.5=정중앙). 추억 여행은 카드가 중앙에 뜨므로 위쪽(0.3)에 잡는다.
  const centerOnNode = (node: UniverseNode, smooth: boolean, focusY = 0.5) => {
    const vp = viewportRef.current?.getBoundingClientRect();
    const el = canvasElRef.current;
    if (!vp || !el) return;
    const s = 1.35;
    transformRef.current = { scale: s, tx: vp.width / 2 - node.x * s, ty: vp.height * focusY - node.y * s };
    el.style.transition = smooth ? "transform 0.9s cubic-bezier(.22,.9,.32,1)" : "";
    el.style.transform = `translate(${transformRef.current.tx}px, ${transformRef.current.ty}px) scale(${s})`;
    applyParallaxRef.current(transformRef.current.tx, transformRef.current.ty, smooth);
  };

  useEffect(() => {
    if (tourIdx === null) {
      if (canvasElRef.current) canvasElRef.current.style.transition = "";
      return;
    }
    const node = tourNodes[tourIdx];
    if (!node) {
      setTourIdx(null);
      return;
    }
    centerOnNode(node, true, 0.3); // 카드가 중앙에 뜨므로 별은 위쪽에
    if (node.memory) markSeen(node.memory.id);
    const timer = setTimeout(() => {
      setTourIdx((i) => (i === null ? null : i + 1 < tourNodes.length ? i + 1 : null));
    }, 3200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourIdx, tourNodes]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const MIN = 0.45;
    const MAX = 2.4;

    const apply = () => {
      const { scale, tx, ty } = transformRef.current;
      if (canvasElRef.current) {
        canvasElRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      }
      applyParallaxRef.current(tx, ty, false);
    };

    const dist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onStart = (e: TouchEvent) => {
      stopTourRef.current();
      if (canvasElRef.current) canvasElRef.current.style.transition = "";
      if (e.touches.length === 2) {
        const { scale } = transformRef.current;
        gestureRef.current = {
          type: "pinch",
          startDist: dist(e.touches),
          startScale: scale,
          px: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          py: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          cx: transformRef.current.tx,
          cy: transformRef.current.ty,
        };
      } else if (e.touches.length === 1) {
        gestureRef.current = {
          type: "pan",
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: transformRef.current.tx,
          startTy: transformRef.current.ty,
        };
        draggedRef.current = false;
      }
    };

    const onMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (!g) return;
      e.preventDefault();
      if (g.type === "pinch" && e.touches.length === 2) {
        const ratio = dist(e.touches) / g.startDist;
        const next = Math.min(MAX, Math.max(MIN, g.startScale * ratio));
        const k = next / g.startScale;
        transformRef.current.scale = next;
        transformRef.current.tx = g.px - (g.px - g.cx) * k;
        transformRef.current.ty = g.py - (g.py - g.cy) * k;
        apply();
      } else if (g.type === "pan" && e.touches.length === 1) {
        const dx = e.touches[0].clientX - g.startX;
        const dy = e.touches[0].clientY - g.startY;
        if (Math.abs(dx) + Math.abs(dy) > 8) draggedRef.current = true;
        transformRef.current.tx = g.startTx + dx;
        transformRef.current.ty = g.startTy + dy;
        apply();
      }
    };

    const onEnd = () => {
      gestureRef.current = null;
    };

    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  // 최초 진입: 멀리서 "후욱" 다가오는 줌 전환 — 나의 세계에서 이 우주로 빨려 들어오는 느낌
  const enteredRef = useRef(false);
  useEffect(() => {
    if (!layout || !viewportRef.current || !canvasElRef.current) return;
    const vp = viewportRef.current.getBoundingClientRect();
    const el = canvasElRef.current;

    if (!enteredRef.current) {
      enteredRef.current = true;
      const s0 = 0.45;
      const start = { scale: s0, tx: vp.width / 2 - layout.center * s0, ty: vp.height / 2 - layout.center * s0 };
      transformRef.current = start;
      el.style.transition = "";
      el.style.transform = `translate(${start.tx}px, ${start.ty}px) scale(${s0})`;
      applyParallaxRef.current(start.tx, start.ty, false);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const end = { scale: 1, tx: vp.width / 2 - layout.center, ty: vp.height / 2 - layout.center };
          transformRef.current = end;
          el.style.transition = "transform 1.1s cubic-bezier(.2,.85,.3,1)";
          el.style.transform = `translate(${end.tx}px, ${end.ty}px) scale(1)`;
          applyParallaxRef.current(end.tx, end.ty, true);
        }),
      );
      return;
    }

    // 데이터 갱신으로 캔버스 크기만 변한 경우 — 즉시 재센터
    transformRef.current = { scale: 1, tx: vp.width / 2 - layout.center, ty: vp.height / 2 - layout.center };
    el.style.transition = "";
    el.style.transform = `translate(${transformRef.current.tx}px, ${transformRef.current.ty}px) scale(1)`;
    applyParallaxRef.current(transformRef.current.tx, transformRef.current.ty, false);
  }, [layout?.canvas]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["museMemory", characterId] });
    // 다이아 소비 액션 뒤 헤더 잔액 동기화
    qc.invalidateQueries({ queryKey: ["balance"] });
  };

  const handleError = (err: unknown, fallback: string) => {
    const code = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "";
    if (code === "INSUFFICIENT_BALANCE" || isInsufficientBalanceError?.(err)) {
      showDiamondShortage();
      return;
    }
    if (code === "CREATOR_PAID_DIAMOND_REQUIRED") {
      // 공식 뮤즈 기억·호칭 소비는 유료 다이아 전용
      void showPaidDiamondRequired();
      return;
    }
    const map: Record<string, string> = {
      MUSE_MEMORY_CORE_SLOT_FULL: t("universe.error.coreSlotFull"),
      MUSE_MEMORY_DUPLICATED: t("universe.error.duplicated"),
      MUSE_MEMORY_INVALID_CONTENT: t("universe.error.invalidContent"),
      MUSE_MEMORY_NOT_FOUND: t("universe.error.notFound"),
      MUSE_MEMORY_CALL_NAME_NOT_ALLOWED: t(LOCKED_NOTICE_KEY),
      MUSE_MEMORY_EDIT_NOT_ALLOWED: t(LOCKED_NOTICE_KEY),
    };
    setNotice(map[code] ?? fallback);
  };

  // 크리에이터(공식 뮤즈) 팬 자율 설정 허용 여부 — 일반 뮤즈/구버전 서버는 항상 허용
  const canSetCallName = data?.permissions?.canSetCallName ?? true;
  const canEditMemory = data?.permissions?.canEditMemory ?? true;

  const doPromote = async (memory: MuseMemoryItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await promoteMuseMemory(characterId, memory.id);
      setSelected(null);
      setNotice(memory.status === "FADED" ? t("universe.notice.revived") : t("universe.notice.promoted"));
      invalidate();
    } catch (err) {
      handleError(err, t("universe.error.promoteFail"));
    } finally {
      setBusy(false);
    }
  };

  const doEdit = async (memory: MuseMemoryItem, content: string) => {
    if (busy || !content.trim()) return;
    setBusy(true);
    try {
      await updateMuseMemory(characterId, memory.id, content.trim());
      setSelected(null);
      setNotice(t("universe.notice.edited"));
      invalidate();
    } catch (err) {
      handleError(err, t("universe.error.editFail"));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (memory: MuseMemoryItem) => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteMuseMemory(characterId, memory.id);
      setSelected(null);
      setNotice(t("universe.notice.deleted"));
      invalidate();
    } catch (err) {
      handleError(err, t("universe.error.deleteFail"));
    } finally {
      setBusy(false);
    }
  };

  // 기존 상세(친밀도·속마음·대화하기) — 화면 안 바텀시트
  const relSheetIdRef = useRef("");
  const openRelationshipSheet = () => {
    // 속마음 확인 처리 — 알럿 해제 + 쿨다운 시작
    if (relItem?.impression) {
      try {
        localStorage.setItem(heartSeenKey, JSON.stringify({ impression: relItem.impression, seenAt: Date.now() }));
      } catch {
        // storage 불가 환경 무시
      }
      setHasNewImpression(false);
    }
    relSheetIdRef.current = openBottomSheet({
      content: <RelationshipDetailSheetBody characterId={characterId} onClose={() => closeById(relSheetIdRef.current)} />,
      midHeightPercent: 62,
      maxHeightPercent: 92,
    });
  };

  // ── 패널 시트들 — 앱 표준 Drawer(포털 바텀시트) 사용 ──
  // 커스텀 인시트는 당겨서-새로고침 오작동이 있어 제거. 패널들은 자체적으로 데이터를 읽는다.
  const panelIdRef = useRef("");
  const closePanel = () => closeById(panelIdRef.current);
  const layoutLiveRef = useRef(layout);
  useEffect(() => {
    layoutLiveRef.current = layout;
  }, [layout]);

  const openSheet = (content: React.ReactNode, mid = 62) => {
    setSelected(null);
    panelIdRef.current = openBottomSheet({
      content,
      midHeightPercent: mid,
      maxHeightPercent: 92,
      backgroundColor: "#0B1024", // 우주와 이어지는 다크 시트
    });
  };

  const openAddSheet = () => {
    // 크리에이터가 기억 생성·수정을 허용하지 않으면 잠금 안내만
    if (!canEditMemory) {
      setNotice(t(LOCKED_NOTICE_KEY));
      return;
    }
    openSheet(
      <AddMemoryPanel
        characterId={characterId}
        onDone={(msg) => {
          closePanel();
          setNotice(msg);
          invalidate();
        }}
        onError={handleError}
      />,
    );
  };

  const openListSheet = () =>
    openSheet(
      <MemoryListPanel
        characterId={characterId}
        canAdd={canEditMemory}
        onAdd={() => {
          closePanel();
          openAddSheet();
        }}
        onPick={(m) => {
          closePanel();
          const node = layoutLiveRef.current?.nodes.find((n) => n.memory?.id === m.id) ?? null;
          if (node) {
            markSeen(m.id);
            setSelected(node);
          }
        }}
      />,
      70,
    );

  const openCallNameSheet = () => {
    // 허용 안 됨 + 이미 새긴 호칭도 없음 → 잠금 안내만 (기존 호칭이 있으면 지우기를 위해 시트는 연다)
    if (!canSetCallName && !data?.callName) {
      setNotice(t(LOCKED_NOTICE_KEY));
      return;
    }
    openSheet(
      <CallNamePanel
        characterId={characterId}
        onDone={(msg) => {
          closePanel();
          setNotice(msg);
          invalidate();
        }}
        onError={handleError}
      />,
    );
  };

  const openHelpSheet = () => openSheet(<HelpPanel />, 76);

  // 스냅샷 갤러리 — 채팅에서 직접 생성한 순간들 (대화 요약·뮤즈 감정과 함께)
  const openSnapshotSheet = () =>
    openSheet(
      <SnapshotGalleryPanel
        characterId={characterId}
        onNotice={(msg) => {
          closePanel();
          setNotice(msg);
        }}
      />,
      70,
    );

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 2600);
    return () => clearTimeout(t);
  }, [notice]);

  const activeCount = data?.memories.filter((m) => m.status === "ACTIVE").length ?? 0;
  const fadedCount = data?.memories.filter((m) => m.status === "FADED").length ?? 0;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
        // 나의 세계(#0a0b14)와 같은 색 계열 — 화면 전환 시 같은 우주 속에서 이어지는 느낌
        background: "radial-gradient(150% 100% at 50% 0%, #14163a 0%, #0a0b14 52%, #06070f 100%)",
      }}
    >
      {/* 성운 */}
      <Box sx={{ position: "absolute", top: "-10%", left: "-16%", width: "70%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(80,120,255,0.13) 0%, transparent 70%)", filter: "blur(30px)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: "-6%", right: "-12%", width: "60%", height: "44%", borderRadius: "50%", background: "radial-gradient(circle, rgba(60,220,190,0.08) 0%, transparent 70%)", filter: "blur(30px)", pointerEvents: "none" }} />

      {/* 배경 별 — 파라락스 2겹 */}
      <Box ref={bgFarRef} sx={{ position: "absolute", inset: "-25%", pointerEvents: "none", willChange: "transform" }}>
        {STARS_FAR.map((s, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: s.top,
              left: s.left,
              width: 1.5,
              height: 1.5,
              borderRadius: "50%",
              bgcolor: "rgba(190,215,255,0.5)",
              animation: "museTwinkle 3.4s ease-in-out infinite",
              animationDelay: `${(i % 7) * 0.4}s`,
              "@keyframes museTwinkle": { "0%, 100%": { opacity: 0.12 }, "50%": { opacity: 0.7 } },
            }}
          />
        ))}
      </Box>
      <Box ref={bgNearRef} sx={{ position: "absolute", inset: "-25%", pointerEvents: "none", willChange: "transform" }}>
        {STARS_NEAR.map((s, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: s.top,
              left: s.left,
              width: 2.5,
              height: 2.5,
              borderRadius: "50%",
              bgcolor: "rgba(230,240,255,0.9)",
              boxShadow: "0 0 4px rgba(210,225,255,0.8)",
              animation: "museTwinkle 2.6s ease-in-out infinite",
              animationDelay: `${(i % 5) * 0.5}s`,
            }}
          />
        ))}
      </Box>

      {/* ── 우주 캔버스 ── */}
      <Box ref={viewportRef} sx={{ position: "absolute", inset: 0, touchAction: "none" }}>
        {layout && data && (
          <Box
            ref={canvasElRef}
            sx={{ position: "absolute", width: layout.canvas, height: layout.canvas, transformOrigin: "0 0", willChange: "transform" }}
          >
            {/* LINKS — 시냅스처럼 이어지는 연관 기억 */}
            {linksOn && links.length > 0 && (
              <svg width={layout.canvas} height={layout.canvas} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                {links.map((l) => (
                  <line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="rgba(111,227,255,0.16)" strokeWidth={1} />
                ))}
              </svg>
            )}

            {/* 먼지 입자 — 성단 구름의 볼륨 */}
            {layout.dust.map((p, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: p.x,
                  top: p.y,
                  width: p.s,
                  height: p.s,
                  borderRadius: "50%",
                  bgcolor: p.color,
                  opacity: p.o,
                  pointerEvents: "none",
                  animation: "museDust 3.6s ease-in-out infinite",
                  animationDelay: `${p.d}s`,
                  "@keyframes museDust": {
                    "0%, 100%": { transform: "scale(1)", filter: "brightness(0.9)" },
                    "50%": { transform: "scale(1.25)", filter: "brightness(1.4)" },
                  },
                }}
              />
            ))}

            {/* 중심 — 뮤즈 프로필. 이 캐릭터로부터 모든 기억이 파생된다 */}
            <Box sx={{ position: "absolute", left: layout.center, top: layout.center, transform: "translate(-50%, -50%)", zIndex: 3 }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 160, damping: 15 }} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
                {/* 플레어 */}
                <Box sx={{ position: "absolute", left: "50%", top: 41, width: 200, height: 1.5, transform: "translate(-50%,-50%)", background: "linear-gradient(90deg, transparent, rgba(220,235,255,0.5), transparent)", pointerEvents: "none" }} />
                {/* 코로나 글로우 */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 41,
                    width: 150,
                    height: 150,
                    transform: "translate(-50%,-50%)",
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(190,215,255,0.22) 0%, rgba(190,215,255,0.06) 48%, transparent 72%)",
                    filter: "blur(4px)",
                    pointerEvents: "none",
                    animation: "museAura 4s ease-in-out infinite",
                    "@keyframes museAura": {
                      "0%, 100%": { opacity: 0.7, transform: "translate(-50%,-50%) scale(1)" },
                      "50%": { opacity: 1, transform: "translate(-50%,-50%) scale(1.1)" },
                    },
                  }}
                />
                {/* 뮤즈 아바타 — 탭하면 마음(관계) 시트 */}
                <ButtonBase
                  onClick={() => {
                    if (draggedRef.current) return;
                    setSelected(null);
                    openRelationshipSheet();
                  }}
                  sx={{
                    width: 82,
                    height: 82,
                    borderRadius: "50%",
                    border: "2px solid rgba(220,235,255,0.85)",
                    boxShadow: "0 0 26px rgba(200,222,255,0.7), 0 0 80px rgba(170,200,255,0.3)",
                    overflow: "hidden",
                    bgcolor: "#101a30",
                  }}
                >
                  {museAvatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={museAvatar} alt={museName} draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#DCE8FF" }}>{museName?.slice(0, 2) || "MU"}</Typography>
                  )}
                </ButtonBase>
                {/* 호칭 필 — "뮤즈가 나를 부르는 이름" (탭해서 새기기/변경) */}
                {/* 엄지로 누르기 쉽게 — 필 주변까지 넉넉한 터치 영역 */}
                <ButtonBase
                  onClick={() => {
                    if (draggedRef.current) return;
                    openCallNameSheet();
                  }}
                  sx={{ mt: "4px", p: "6px", borderRadius: "999px" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      px: "16px",
                      height: 36,
                      borderRadius: "999px",
                      border: `1.5px solid ${data.callName ? "rgba(255,201,74,0.6)" : HUD.panelBorder}`,
                      bgcolor: "rgba(5,11,24,0.85)",
                      maxWidth: 210,
                    }}
                  >
                    {!canSetCallName && !data.callName && <Icon name="icLock" size={13} color="rgba(190,205,235,0.7)" />}
                    <Typography
                      sx={{
                        ...TEMP_TEXT_BOLD_XS,
                        fontSize: 13.5,
                        color: data.callName ? HUD.amber : "rgba(190,205,235,0.85)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {data.callName ? f(t("universe.callName.current"), [data.callName]) : t("universe.callName.set")}
                    </Typography>
                  </Box>
                </ButtonBase>
              </motion.div>
            </Box>

            {/* 기억/약속 입자 노드 */}
            {layout.nodes.map((node, i) => (
              <Box key={node.key} sx={{ position: "absolute", left: node.x, top: node.y, transform: "translate(-50%, -50%)", zIndex: node.dim ? 1 : 2 }}>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: node.dim ? 0.4 : 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 18, delay: Math.min(1.1, 0.1 + i * 0.02) }}
                >
                  <ButtonBase
                    onClick={() => {
                      if (draggedRef.current) return;
                      stopTourRef.current();
                      const mid = node.memory?.id;
                      if (mid && !seenIds.has(mid)) {
                        markSeen(mid);
                        setNotice(t("universe.notice.discovered"));
                      }
                      setSelected(node);
                    }}
                    aria-label={t("universe.node.memory")}
                    sx={{
                      width: node.size + 16,
                      height: node.size + 16,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* 부드러운 외곽 광륜 — 빛망울(보케) 느낌 */}
                    {!node.dim && (
                      <Box
                        sx={{
                          position: "absolute",
                          width: node.size * 2.4,
                          height: node.size * 2.4,
                          borderRadius: "50%",
                          background: `radial-gradient(circle, ${node.color}30 0%, transparent 68%)`,
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    {/* 미발견 링 */}
                    {node.memory && !seenIds.has(node.memory.id) && !node.dim && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          border: "1px dashed rgba(255,255,255,0.5)",
                          animation: "museUnseen 2.2s ease-in-out infinite",
                          "@keyframes museUnseen": {
                            "0%, 100%": { opacity: 0.2, transform: "scale(0.9)" },
                            "50%": { opacity: 0.8, transform: "scale(1.05)" },
                          },
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: node.size,
                        height: node.size,
                        borderRadius: "50%",
                        background: `radial-gradient(circle at 38% 32%, rgba(255,255,255,0.95) 0%, ${node.color} 50%, ${node.color}00 115%)`,
                        boxShadow:
                          node.memory && isCore(node.memory)
                            ? `0 0 12px ${node.color}, 0 0 30px ${node.color}66`
                            : node.recalledRecently
                              ? `0 0 12px ${node.color}CC, 0 0 24px ${node.color}55`
                              : `0 0 ${node.dim ? 3 : 8}px ${node.color}${node.dim ? "44" : "99"}`,
                        ...(node.memory &&
                          isCore(node.memory) && {
                            animation: "museCorePulse 2.4s ease-in-out infinite",
                            "@keyframes museCorePulse": {
                              "0%, 100%": { transform: "scale(1)" },
                              "50%": { transform: "scale(1.15)" },
                            },
                          }),
                        ...(node.recalledRecently &&
                          !(node.memory && isCore(node.memory)) && {
                            animation: "museRecalled 1.9s ease-in-out infinite",
                            "@keyframes museRecalled": {
                              "0%, 100%": { filter: "brightness(1)" },
                              "50%": { filter: "brightness(1.6)" },
                            },
                          }),
                      }}
                    />
                  </ButtonBase>
                </motion.div>
              </Box>
            ))}
          </Box>
        )}

        {isLoading && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={26} sx={{ color: HUD.cyan }} />
          </Box>
        )}

        {/* 기억 생성중 */}
        {data?.isGenerating && layout && layout.nodes.length === 0 && (
          <Box sx={{ position: "absolute", left: 0, right: 0, top: "56%", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", px: 4, pointerEvents: "none" }}>
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 10.5, letterSpacing: "0.1em", color: HUD.teal, animation: "museBlink 1.2s step-end infinite", "@keyframes museBlink": { "50%": { opacity: 0.35 } } }}>
              {t("universe.generating.tag")}
            </Typography>
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: "rgba(226,236,255,0.92)", textAlign: "center" }}>
              {t("universe.generating.title")}
            </Typography>
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: "rgba(178,195,225,0.68)", textAlign: "center", whiteSpace: "pre-line" }}>
              {t("universe.generating.subtitle")}
            </Typography>
          </Box>
        )}

        {/* 빈 우주 — 이제 시작 */}
        {data && !data.isGenerating && layout && layout.nodes.length === 0 && (
          <Box sx={{ position: "absolute", left: 0, right: 0, top: "60%", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", px: 4, pointerEvents: "none" }}>
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: "rgba(222,232,255,0.88)", textAlign: "center" }}>
              {t("universe.empty.title")}
            </Typography>
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: "rgba(178,195,225,0.62)", textAlign: "center" }}>
              {t("universe.empty.subtitle")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 비네트 */}
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(115% 88% at 50% 44%, transparent 58%, rgba(1,2,6,0.6) 100%)", zIndex: 4 }} />

      {/* ── HUD 상단 바 ── */}
      <Box sx={{ position: "absolute", top: "calc(env(safe-area-inset-top, 0px) + 8px)", left: "10px", right: "10px", zIndex: 10 }}>
        <Box sx={{ ...PANEL, display: "flex", alignItems: "center", gap: "10px", px: "12px", py: "10px" }}>
          <ButtonBase onClick={onBackPress} sx={{ width: 36, height: 36, borderRadius: "8px", border: `1px solid ${HUD.panelBorder}` }}>
            <ArrowBackIosNewRoundedIcon sx={{ color: HUD.cyan, fontSize: 17 }} />
          </ButtonBase>
          {museAvatar && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={museAvatar} alt={museName} style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", border: `1px solid ${HUD.panelBorder}` }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 16, color: HUD.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {museName ? f(t("universe.header.title"), [museName]) : t("universe.header.fallback")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "2px" }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: HUD.green, animation: "museBlink2 1.6s ease-in-out infinite", "@keyframes museBlink2": { "50%": { opacity: 0.2 } } }} />
              {data && (
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12, color: HUD.dim }}>
                  {f(t("universe.header.stats"), [activeCount, fadedCount])}
                </Typography>
              )}
            </Box>
          </Box>
          <ButtonBase
            onClick={openHelpSheet}
            aria-label={t("mypage.help")}
            sx={{ width: 36, height: 36, borderRadius: "8px", border: `1px solid ${HUD.panelBorder}` }}
          >
            <Typography sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: HUD.cyan }}>?</Typography>
          </ButtonBase>
          <ButtonBase
            onClick={openRelationshipSheet}
            sx={{
              position: "relative",
              height: 36,
              px: "12px",
              borderRadius: "8px",
              border: `1px solid ${heartColor}77`,
              bgcolor: `${heartColor}14`,
              gap: "5px",
            }}
          >
            <FavoriteRoundedIcon sx={{ color: heartColor, fontSize: 15 }} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: heartColor, fontSize: 13 }}>{t("universe.heart")}</Typography>
            {/* 새 속마음 알럿 */}
            {hasNewImpression && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  bgcolor: "#FF3B30",
                  border: "1.5px solid #0B1024",
                  animation: "museAlert 1.4s ease-in-out infinite",
                  "@keyframes museAlert": { "0%, 100%": { transform: "scale(1)" }, "50%": { transform: "scale(1.25)" } },
                }}
              />
            )}
          </ButtonBase>
        </Box>

        {/* 서브 상태줄 — 좌: 코어 슬롯, 우: 연결선/추억여행 토글 (한 줄 통합 → 겹침 없음) */}
        {data && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "8px", gap: "8px" }}>
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: HUD.amber, flexShrink: 0 }}>
              {f(t("universe.core.count"), [data.slots.core.used, data.slots.core.max])}
              <Box component="span" sx={{ color: HUD.dim, ml: "8px", fontWeight: 500 }}>
                {intimacyBrandLabel
                  ? f(t("universe.core.level_custom"), [data.intimacyLevel, data.intimacy, intimacyBrandLabel])
                  : f(t("universe.core.level"), [data.intimacyLevel, data.intimacy])}
              </Box>
            </Typography>
            <Box sx={{ display: "flex", gap: "7px" }}>
              <ButtonBase onClick={() => setLinksOn((v) => !v)} sx={{ ...PANEL, height: 34, px: "12px", borderRadius: "8px" }}>
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: linksOn ? HUD.teal : HUD.dim }}>
                  {f(t("universe.links"), [linksOn ? "ON" : "OFF"])}
                </Typography>
              </ButtonBase>
              {tourNodes.length > 1 && (
                <ButtonBase
                  onClick={() => {
                    if (tourIdx !== null) {
                      stopTourRef.current();
                    } else {
                      setSelected(null);
                      setTourIdx(0);
                    }
                  }}
                  sx={{ ...PANEL, height: 34, px: "12px", borderRadius: "8px" }}
                >
                  <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: tourIdx !== null ? HUD.amber : HUD.dim }}>
                    {tourIdx !== null ? t("universe.tour.stop") : t("universe.tour.start")}
                  </Typography>
                </ButtonBase>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── TOUR 카드 ── */}
      <AnimatePresence>
        {tourIdx !== null && tourNodes[tourIdx]?.memory && (
          <motion.div
            key={`tour-${tourIdx}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            style={{ position: "absolute", left: 16, right: 16, top: "48%", zIndex: 21 }}
          >
            <ButtonBase
              onClick={() => setTourIdx((i) => (i === null ? null : i + 1 < tourNodes.length ? i + 1 : null))}
              sx={{ ...PANEL, display: "block", width: "100%", textAlign: "left", p: "12px 14px", borderColor: "rgba(255,201,74,0.4)" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "6px" }}>
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 10.5, color: HUD.amber, flex: 1 }}>
                  {f(t("universe.tour.progress"), [tourIdx + 1, tourNodes.length, new Date(tourNodes[tourIdx].memory!.createdAt).toLocaleDateString("ko-KR")])}
                </Typography>
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 9.5, color: HUD.dim }}>{t("universe.tour.tapNext")}</Typography>
              </Box>
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, fontSize: 15, color: HUD.text, wordBreak: "break-word" }}>
                {toFirstPerson(tourNodes[tourIdx].memory!.content)}
              </Typography>
            </ButtonBase>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── 하단 액션 바 — 상세 카드/추억 여행 중에는 숨겨서 겹침 방지 ── */}
      <Box
        sx={{
          position: "absolute",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
          left: "10px",
          right: "10px",
          display: "flex",
          gap: "8px",
          zIndex: 10,
          opacity: selected || tourIdx !== null ? 0 : 1,
          pointerEvents: selected || tourIdx !== null ? "none" : "auto",
          transition: "opacity 0.25s ease",
        }}
      >
        <ButtonBase onClick={openListSheet} sx={{ ...PANEL, flex: 1, height: 54, borderRadius: "12px", gap: "7px" }}>
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 15, color: HUD.cyan }}>{t("universe.list.title")}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 13, color: HUD.dim }}>{activeCount + fadedCount}</Typography>
        </ButtonBase>
        <ButtonBase onClick={openSnapshotSheet} sx={{ ...PANEL, flex: 1, height: 54, borderRadius: "12px", gap: "6px" }}>
          <Icon name="icCamera" size={16} color={HUD.teal} />
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 15, color: HUD.teal }}>
            {t("museAsset.gallery.title")}
          </Typography>
        </ButtonBase>
        <ButtonBase
          onClick={openAddSheet}
          sx={{
            flex: 1,
            height: 54,
            borderRadius: "12px",
            border: canEditMemory ? "1px solid rgba(255,201,74,0.55)" : `1px solid ${HUD.panelBorder}`,
            background: canEditMemory
              ? "linear-gradient(180deg, rgba(255,201,74,0.2) 0%, rgba(255,180,84,0.1) 100%)"
              : "rgba(255,255,255,0.04)",
            backdropFilter: "blur(10px)",
            gap: "5px",
          }}
        >
          {canEditMemory ? (
            <AddRoundedIcon sx={{ color: HUD.amber, fontSize: 20 }} />
          ) : (
            <Icon name="icLock" size={15} color={HUD.dim} />
          )}
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 15, color: canEditMemory ? HUD.amber : HUD.dim }}>
            {t("universe.addCore")}
          </Typography>
        </ButtonBase>
      </Box>

      {/* ── 토스트 ── */}
      <AnimatePresence>
        {notice && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{ position: "absolute", top: "calc(env(safe-area-inset-top, 0px) + 116px)", left: 0, right: 0, display: "flex", justifyContent: "center", zIndex: 30, pointerEvents: "none" }}
          >
            <Box sx={{ ...PANEL, px: "14px", py: "8px", display: "flex", alignItems: "center", gap: "7px" }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: HUD.teal }} />
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: HUD.text }}>{notice}</Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── 노드 상세 패널 (터미널 스타일) ── */}
      <AnimatePresence>
        {selected && data && (
          <NodeDetailPanel
            key={selected.key}
            node={selected}
            data={data}
            busy={busy}
            canEditMemory={canEditMemory}
            onLocked={() => setNotice(t(LOCKED_NOTICE_KEY))}
            onClose={() => setSelected(null)}
            onPromote={doPromote}
            onEdit={doEdit}
            onDelete={doDelete}
          />
        )}
      </AnimatePresence>

    </Box>
  );
}

// ============================================================
// 노드 상세 패널 — 레퍼런스의 CONVERT / DELETE MEMORY / CONFIRM ERASURE
// ============================================================

function NodeDetailPanel({
  node,
  data,
  busy,
  canEditMemory,
  onLocked,
  onClose,
  onPromote,
  onEdit,
  onDelete,
}: {
  node: UniverseNode;
  data: UniverseData;
  busy: boolean;
  canEditMemory: boolean;
  onLocked: () => void;
  onClose: () => void;
  onPromote: (m: MuseMemoryItem) => void;
  onEdit: (m: MuseMemoryItem, content: string) => void;
  onDelete: (m: MuseMemoryItem) => void;
}) {
  const { t, f } = useTranslator();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const m = node.memory ?? null;

  const headerLabel =
    node.kind === "promise"
      ? t("universe.node.promise")
      : m && isCore(m)
        ? t("universe.node.core")
        : m?.status === "FADED"
          ? t("universe.node.faded")
          : t("universe.node.memory");

  const deletePrice = m && !isCore(m) && m.source === "AI" ? data.prices.deleteAi : data.prices.deleteUser;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 28 }}
      transition={{ type: "spring", stiffness: 300, damping: 28 }}
      style={{ position: "absolute", left: 10, right: 10, bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)", zIndex: 20 }}
    >
      <Box sx={{ ...PANEL, borderColor: `${node.color}66`, overflow: "hidden" }}>
        {/* 패널 헤더 — 터미널 타이틀 바 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", px: "12px", py: "7px", borderBottom: `1px solid rgba(110,200,255,0.15)` }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: node.color, boxShadow: `0 0 7px ${node.color}` }} />
          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: node.color, flex: 1 }}>
            {headerLabel}
          </Typography>
          {m && (
            <Typography sx={{ fontFamily: MONO, fontSize: 11, color: HUD.dim }}>
              {new Date(m.createdAt).toLocaleDateString("ko-KR")}
            </Typography>
          )}
          <ButtonBase onClick={onClose} sx={{ width: 30, height: 30, borderRadius: "8px", border: `1px solid ${HUD.panelBorder}` }}>
            <CloseRoundedIcon sx={{ color: HUD.dim, fontSize: 17 }} />
          </ButtonBase>
        </Box>

        <Box sx={{ p: "12px 14px" }}>
          {editing && m ? (
            <Box sx={{ borderRadius: "4px", border: "1px solid rgba(255,201,74,0.4)", bgcolor: "rgba(255,201,74,0.05)", p: "9px 11px", mb: "10px" }}>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value.slice(0, data.limits.memoryMaxChars))}
                rows={2}
                style={{ width: "100%", background: "transparent", border: "none", outline: "none", resize: "none", color: HUD.text, fontSize: 15.5, lineHeight: 1.55, fontFamily: "inherit" }}
              />
            </Box>
          ) : (
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, fontSize: 15.5, lineHeight: 1.55, color: HUD.text, mb: "9px", wordBreak: "break-word" }}>
              {toFirstPerson(node.kind === "promise" ? (node.promiseContent ?? "") : (m?.content ?? ""))}
            </Typography>
          )}

          {m && (
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12.5, color: "rgba(178,195,225,0.75)", mb: "13px" }}>
              {m.recallCount > 0
                ? `${f(t("universe.node.recalled"), [m.recallCount])}${m.lastRecalledAt ? ` · ${formatRelative(m.lastRecalledAt, t, f)}` : ""}`
                : t("universe.node.notRecalled")}
            </Typography>
          )}

          {node.kind === "promise" ? (
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12.5, color: "rgba(180,195,228,0.65)" }}>
              {t("universe.node.promiseDesc")}
            </Typography>
          ) : m && editing ? (
            <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <ButtonBase
                disabled={busy || !editText.trim()}
                onClick={() => onEdit(m, editText)}
                sx={{ height: 40, px: "20px", borderRadius: "10px", bgcolor: HUD.amber, opacity: busy || !editText.trim() ? 0.6 : 1 }}
              >
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: "#3A2A00" }}>{t("chat.save")}</Typography>
              </ButtonBase>
              <ButtonBase onClick={() => setEditing(false)} sx={{ height: 40, px: "16px", borderRadius: "10px", border: `1px solid ${HUD.panelBorder}` }}>
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: HUD.dim }}>{t("common.cancel")}</Typography>
              </ButtonBase>
            </Box>
          ) : m && !confirmDelete ? (
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {!isCore(m) && (
                <HudButton
                  color={HUD.teal}
                  disabled={busy}
                  onClick={() => onPromote(m)}
                  label={m.status === "FADED" ? t("universe.node.revive") : t("universe.addCore")}
                  price={data.prices.promoteToCore}
                />
              )}
              {isCore(m) && (
                <HudButton
                  color={canEditMemory ? HUD.amber : HUD.dim}
                  disabled={busy}
                  locked={!canEditMemory}
                  onClick={() => {
                    // 크리에이터가 기억 수정을 허용하지 않으면 잠금 안내만
                    if (!canEditMemory) {
                      onLocked();
                      return;
                    }
                    setEditText(m.content);
                    setEditing(true);
                  }}
                  label={t("universe.node.edit")}
                />
              )}
              <HudButton color={HUD.red} disabled={busy} onClick={() => setConfirmDelete(true)} label={t("universe.node.delete")} price={deletePrice} />
            </Box>
          ) : m && confirmDelete ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: HUD.red, flex: 1 }}>
                {t("universe.node.deleteConfirm")}
              </Typography>
              <ButtonBase
                disabled={busy}
                onClick={() => onDelete(m)}
                sx={{ height: 40, px: "18px", borderRadius: "10px", bgcolor: HUD.red, opacity: busy ? 0.6 : 1 }}
              >
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: "#fff" }}>{t("universe.node.delete")}</Typography>
              </ButtonBase>
              <ButtonBase onClick={() => setConfirmDelete(false)} sx={{ height: 40, px: "15px", borderRadius: "10px", border: `1px solid ${HUD.panelBorder}` }}>
                <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: HUD.dim }}>{t("common.cancel")}</Typography>
              </ButtonBase>
            </Box>
          ) : null}
        </Box>
      </Box>
    </motion.div>
  );
}

function HudButton({
  label,
  price,
  color,
  disabled,
  locked,
  onClick,
}: {
  label: string;
  price?: number;
  color: string;
  disabled?: boolean;
  /** 크리에이터 미허용 잠금 — 자물쇠 아이콘 표시, 클릭은 안내용으로 살려둔다 */
  locked?: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      sx={{ height: 42, px: "15px", borderRadius: "10px", border: `1px solid ${color}88`, bgcolor: `${color}16`, gap: "6px", opacity: disabled ? 0.55 : 1 }}
    >
      {locked && <Icon name="icLock" size={13} color={color} />}
      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color }}>{label}</Typography>
      {!locked && price !== undefined && price > 0 && (
        <>
          <Icon name="icDiamondFilled" size={12} color={TEMP_FG_DIAMOND} />
          <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700, color: TEMP_FG_DIAMOND }}>{formatDiamond(price)}</Typography>
        </>
      )}
    </ButtonBase>
  );
}

// ============================================================
// 패널 공통 — 앱 표준 Drawer(포털 바텀시트) 안에서 쓰는 다크 카드
// 패널은 자체적으로 캐시된 우주 데이터를 읽는다 (포털 스냅샷 문제 방지)
// ============================================================

function useUniverseData(characterId: string) {
  const { data } = useQuery({
    queryKey: ["museMemory", characterId],
    queryFn: () => getMemoryUniverse(characterId),
    staleTime: 30_000,
  });
  return data ?? null;
}

function PanelShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  // 시트 자체가 우주 톤(#0B1024)이라 카드 없이 자연스럽게 콘텐츠만 —
  // 시트 하단 safe area까지 여백 확보.
  // flex:1로 시트 높이를 정확히 채워야 내부 스크롤 영역(flex:1 리스트)이
  // 시트 밖으로 넘치지 않는다 — vh 고정 높이는 웹뷰에서 시트보다 커져 하단이 잘린다.
  return (
    <Box
      sx={{
        px: "18px",
        pt: "6px",
        pb: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 18px)",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 18, color: HUD.text, mb: subtitle ? "5px" : "14px", flexShrink: 0 }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 13, lineHeight: 1.5, color: "rgba(178,195,228,0.72)", mb: "14px", flexShrink: 0 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
}

// ============================================================
// 기억 목록 — 탭(전체/코어/흐려짐) + 검색 + 새기기
// ============================================================

// label은 i18n 키 — 렌더 시점에 t() 적용
const LIST_TABS = [
  { key: "ALL", label: "home.filter.all" },
  { key: "CORE", label: "universe.list.tabCore" },
  { key: "FADED", label: "universe.list.tabFaded" },
] as const;

function MemoryListPanel({
  characterId,
  canAdd,
  onPick,
  onAdd,
}: {
  characterId: string;
  canAdd: boolean;
  onPick: (m: MuseMemoryItem) => void;
  onAdd: () => void;
}) {
  const { t } = useTranslator();
  const data = useUniverseData(characterId);
  const [tab, setTab] = useState<(typeof LIST_TABS)[number]["key"]>("ALL");
  const [q, setQ] = useState("");

  if (!data) return null;

  const counts = {
    ALL: data.memories.filter((m) => m.status === "ACTIVE").length,
    CORE: data.memories.filter((m) => isCore(m) && m.status === "ACTIVE").length,
    FADED: data.memories.filter((m) => m.status === "FADED").length,
  };

  const filtered = data.memories.filter((m) => {
    if (tab === "CORE" && !(isCore(m) && m.status === "ACTIVE")) return false;
    if (tab === "FADED" && m.status !== "FADED") return false;
    if (tab === "ALL" && m.status !== "ACTIVE") return false;
    if (q && !m.content.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const colorOf = (m: MuseMemoryItem) =>
    m.status === "FADED"
      ? CLUSTERS.FADED.hues[0]
      : isCore(m)
        ? CLUSTERS.CORE.hues[1]
        : m.bucket === "CURRENT"
          ? CLUSTERS.CURRENT.hues[1]
          : CLUSTERS.STABLE.hues[1];

  return (
    <PanelShell title={t("universe.list.title")} subtitle={t("universe.list.subtitle")}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px", mb: "12px", flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: "7px", flex: 1 }}>
          {LIST_TABS.map((tabDef) => (
            <ButtonBase
              key={tabDef.key}
              onClick={() => setTab(tabDef.key)}
              sx={{
                height: 38,
                px: "14px",
                borderRadius: "10px",
                border: `1px solid ${tab === tabDef.key ? HUD.cyan : "rgba(110,200,255,0.18)"}`,
                bgcolor: tab === tabDef.key ? "rgba(111,227,255,0.1)" : "transparent",
                gap: "5px",
              }}
            >
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: tab === tabDef.key ? HUD.cyan : HUD.dim }}>
                {t(tabDef.label)}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 12, color: tab === tabDef.key ? HUD.cyan : HUD.dim }}>
                {counts[tabDef.key]}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
        <ButtonBase
          onClick={onAdd}
          sx={{
            height: 38,
            px: "13px",
            borderRadius: "10px",
            border: canAdd ? "1px solid rgba(255,201,74,0.55)" : `1px solid ${HUD.panelBorder}`,
            bgcolor: canAdd ? "rgba(255,201,74,0.1)" : "rgba(255,255,255,0.04)",
            gap: "4px",
          }}
        >
          {canAdd ? (
            <AddRoundedIcon sx={{ color: HUD.amber, fontSize: 16 }} />
          ) : (
            <Icon name="icLock" size={13} color={HUD.dim} />
          )}
          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: canAdd ? HUD.amber : HUD.dim }}>{t("universe.list.add")}</Typography>
        </ButtonBase>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: 44,
          flexShrink: 0,
          px: "13px",
          mb: "12px",
          borderRadius: "12px",
          border: "1px solid rgba(110,200,255,0.2)",
          bgcolor: "rgba(111,227,255,0.05)",
        }}
      >
        <SearchRoundedIcon sx={{ color: HUD.dim, fontSize: 19 }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("universe.list.searchPlaceholder")}
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: HUD.text, fontSize: 15 }}
        />
      </Box>

      {/* 남은 시트 높이를 그대로 스크롤 영역으로 — vh 고정은 웹뷰에서 하단 잘림.
          data-scroll-container: 포털 열림 중 useBodyScrollLock의 전역 touchmove 차단 화이트리스트 */}
      <Box data-scroll-container sx={{ overflowY: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
        {filtered.length === 0 && (
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 13, color: HUD.dim, textAlign: "center", py: "26px" }}>
            {t("universe.list.empty")}
          </Typography>
        )}
        {filtered.map((m) => (
          <ButtonBase
            key={m.id}
            onClick={() => onPick(m)}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexShrink: 0,
              gap: "10px",
              textAlign: "left",
              p: "13px 14px",
              borderRadius: "12px",
              border: `1px solid ${colorOf(m)}30`,
              borderLeft: `3px solid ${colorOf(m)}`,
              bgcolor: "rgba(255,255,255,0.03)",
              opacity: m.status === "FADED" ? 0.55 : 1,
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 14, lineHeight: 1.5, color: HUD.text, flex: 1, wordBreak: "break-word" }}>
              {toFirstPerson(m.content)}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 10, color: HUD.dim, flexShrink: 0, mt: "3px" }}>
              {new Date(m.createdAt).toLocaleDateString("ko-KR").replace(/\s/g, "")}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </PanelShell>
  );
}

// ============================================================
// 코어에 새기기 — 추억을 쌓을수록 뮤즈가 나를 더 많이 기억한다
// ============================================================

function AddMemoryPanel({
  characterId,
  onDone,
  onError,
}: {
  characterId: string;
  onDone: (msg: string) => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const { t, f } = useTranslator();
  const data = useUniverseData(characterId);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  if (!data) return null;

  const slot = data.slots.core;
  const slotFull = slot.used >= slot.max;
  const locked = !(data.permissions?.canEditMemory ?? true);

  const submit = async () => {
    if (busy || locked || !content.trim()) return;
    setBusy(true);
    try {
      await addMuseMemory(characterId, content.trim());
      onDone(t("universe.add.done"));
    } catch (err) {
      onError(err, t("universe.error.promoteFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title={t("universe.addCore")} subtitle={t("universe.add.subtitle")}>
      {/* 코어 슬롯 게이지 */}
      <Box sx={{ mb: "14px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 13.5, color: HUD.amber }}>
            {f(t("universe.core.count"), [slot.used, slot.max])}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12, color: "rgba(170,190,225,0.6)" }}>
            {f(t("universe.add.slotHint"), [data.intimacyLevel])}
          </Typography>
        </Box>
        <Box sx={{ height: 6, borderRadius: "3px", bgcolor: "rgba(255,201,74,0.12)", overflow: "hidden" }}>
          <Box
            sx={{
              width: `${Math.min(100, (slot.used / Math.max(1, slot.max)) * 100)}%`,
              height: "100%",
              bgcolor: HUD.amber,
              boxShadow: `0 0 8px ${HUD.amber}`,
            }}
          />
        </Box>
      </Box>

      <Box sx={{ borderRadius: "12px", border: "1px solid rgba(255,201,74,0.35)", bgcolor: "rgba(255,201,74,0.06)", p: "13px 14px", mb: "5px" }}>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, data.limits.memoryMaxChars))}
          placeholder={t("universe.add.placeholder")}
          rows={3}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            resize: "none",
            color: HUD.text,
            fontSize: 15.5,
            lineHeight: 1.55,
            fontFamily: "inherit",
          }}
        />
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: 11, color: HUD.dim, textAlign: "right", mb: "14px" }}>
        {content.length}/{data.limits.memoryMaxChars}
      </Typography>

      <ButtonBase
        disabled={busy || locked || !content.trim() || slotFull}
        onClick={submit}
        sx={{
          height: 54,
          borderRadius: "14px",
          border: `1px solid ${slotFull || locked ? "rgba(255,255,255,0.15)" : "rgba(255,201,74,0.6)"}`,
          background: slotFull || locked ? "rgba(255,255,255,0.05)" : "linear-gradient(180deg, rgba(255,201,74,0.25) 0%, rgba(255,170,60,0.12) 100%)",
          gap: "7px",
          opacity: busy || (!locked && !content.trim()) ? 0.55 : 1,
        }}
      >
        {locked ? (
          <>
            <Icon name="icLock" size={15} color={HUD.dim} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 14, color: HUD.dim }}>{t(LOCKED_NOTICE_KEY)}</Typography>
          </>
        ) : slotFull ? (
          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 14, color: HUD.dim }}>{t("universe.add.slotFullButton")}</Typography>
        ) : (
          <>
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 15.5, color: HUD.amber }}>{t("universe.addCore")}</Typography>
            <Icon name="icDiamondFilled" size={15} color={TEMP_FG_DIAMOND} />
            <Typography sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: TEMP_FG_DIAMOND }}>
              {formatDiamond(data.prices.addCore)}
            </Typography>
          </>
        )}
      </ButtonBase>
    </PanelShell>
  );
}

// ============================================================
// 호칭 — 뮤즈가 나를 부르는 이름
// ============================================================

function CallNamePanel({
  characterId,
  onDone,
  onError,
}: {
  characterId: string;
  onDone: (msg: string) => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const { t, f } = useTranslator();
  const data = useUniverseData(characterId);
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!data) return null;
  const value = name ?? data.callName ?? "";
  const locked = !(data.permissions?.canSetCallName ?? true);

  const submit = async () => {
    if (busy || locked || !value.trim()) return;
    setBusy(true);
    try {
      await setMuseCallName(characterId, value.trim());
      onDone(f(t("universe.callName.done"), [value.trim()]));
    } catch (err) {
      onError(err, t("universe.error.callNameFail"));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setMuseCallName(characterId, null);
      onDone(t("universe.callName.cleared"));
    } catch (err) {
      onError(err, t("universe.error.clearFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelShell title={t("universe.callName.set")} subtitle={t("universe.callName.subtitle")}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: 54,
          px: "15px",
          mb: "16px",
          borderRadius: "12px",
          border: "1px solid rgba(111,227,255,0.35)",
          bgcolor: "rgba(111,227,255,0.06)",
        }}
      >
        <input
          value={value}
          disabled={locked}
          onChange={(e) => setName(e.target.value.slice(0, data.limits.callNameMaxChars))}
          placeholder={t("universe.callName.placeholder")}
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: locked ? HUD.dim : HUD.text, fontSize: 17, fontWeight: 700 }}
        />
        <Typography sx={{ fontFamily: MONO, fontSize: 11, color: HUD.dim }}>
          {value.length}/{data.limits.callNameMaxChars}
        </Typography>
      </Box>

      <ButtonBase
        disabled={busy || locked || !value.trim() || value.trim() === data.callName}
        onClick={submit}
        sx={{
          height: 54,
          borderRadius: "14px",
          border: locked ? `1px solid ${HUD.panelBorder}` : "1px solid rgba(111,227,255,0.55)",
          background: locked ? "rgba(255,255,255,0.04)" : "linear-gradient(180deg, rgba(111,227,255,0.2) 0%, rgba(80,180,255,0.08) 100%)",
          gap: "7px",
          mb: "10px",
          opacity: locked ? 1 : busy || !value.trim() || value.trim() === data.callName ? 0.55 : 1,
        }}
      >
        {locked ? (
          <>
            <Icon name="icLock" size={15} color={HUD.dim} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 14, color: HUD.dim }}>{t(LOCKED_NOTICE_KEY)}</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, fontSize: 15.5, color: HUD.cyan }}>{t("universe.callName.submit")}</Typography>
            <Icon name="icDiamondFilled" size={15} color={TEMP_FG_DIAMOND} />
            <Typography sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: TEMP_FG_DIAMOND }}>
              {formatDiamond(data.prices.setCallName)}
            </Typography>
          </>
        )}
      </ButtonBase>

      {data.callName && (
        <ButtonBase disabled={busy} onClick={clear} sx={{ height: 46, borderRadius: "12px", border: `1px solid ${HUD.panelBorder}` }}>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 13.5, color: HUD.dim }}>{t("universe.callName.clear")}</Typography>
        </ButtonBase>
      )}
    </PanelShell>
  );
}

// ============================================================
// 도움말 — 이 우주가 무엇인지 한눈에
// ============================================================

// title/desc는 i18n 키 — 렌더 시점에 t() 적용
const HELP_ITEMS: { color: string; title: string; desc: string }[] = [
  { color: CLUSTERS.CORE.hues[1], title: "universe.help.core.title", desc: "universe.help.core.desc" },
  { color: CLUSTERS.STABLE.hues[1], title: "universe.help.stable.title", desc: "universe.help.stable.desc" },
  { color: CLUSTERS.CURRENT.hues[1], title: "universe.help.current.title", desc: "universe.help.current.desc" },
  { color: CLUSTERS.PROMISE.hues[1], title: "universe.help.promise.title", desc: "universe.help.promise.desc" },
  { color: CLUSTERS.FADED.hues[0], title: "universe.help.faded.title", desc: "universe.help.faded.desc" },
];

// 제목/설명 i18n 키 쌍 — 렌더 시점에 t() 적용
const HELP_ROWS: { titleKey: string; descKey: string }[] = [
  { titleKey: "universe.help.brightness.title", descKey: "universe.help.brightness.desc" },
  { titleKey: "universe.help.ring.title", descKey: "universe.help.ring.desc" },
  { titleKey: "universe.help.delete.title", descKey: "universe.help.delete.desc" },
  { titleKey: "universe.help.callName.title", descKey: "universe.help.callName.desc" },
  { titleKey: "universe.help.linksTour.title", descKey: "universe.help.linksTour.desc" },
  { titleKey: "universe.help.chatLink.title", descKey: "universe.help.chatLink.desc" },
  { titleKey: "universe.help.official.title", descKey: "universe.help.official.desc" },
];

function HelpPanel() {
  const { t } = useTranslator();
  return (
    <PanelShell title={t("universe.help.title")} subtitle={t("universe.help.subtitle")}>
      <Box data-scroll-container sx={{ display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto", flex: 1, minHeight: 0 }}>
        {HELP_ITEMS.map((item) => (
          <Box key={item.title} sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <Box sx={{ width: 10, height: 10, mt: "4px", borderRadius: "50%", flexShrink: 0, bgcolor: item.color, boxShadow: `0 0 8px ${item.color}` }} />
            <Box>
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 14, color: HUD.text }}>{t(item.title)}</Typography>
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12.5, lineHeight: 1.5, color: "rgba(178,195,225,0.72)", mt: "3px" }}>
                {t(item.desc)}
              </Typography>
            </Box>
          </Box>
        ))}

        <Box sx={{ borderTop: `1px solid rgba(110,200,255,0.15)`, pt: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {HELP_ROWS.map((row) => (
            <Box key={row.titleKey}>
              <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 14, color: HUD.cyan }}>{t(row.titleKey)}</Typography>
              <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12.5, lineHeight: 1.5, color: "rgba(178,195,225,0.72)", mt: "3px" }}>
                {t(row.descKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </PanelShell>
  );
}

// ============================================================
// 스냅샷 갤러리 — 채팅 직접생성 스냅샷 (어떤 대화였는지 + 뮤즈의 감정 + 배경 설정/삭제)
// ============================================================

function SnapshotGalleryPanel({
  characterId,
  onNotice,
}: {
  characterId: string;
  onNotice: (msg: string) => void;
}) {
  const { t } = useTranslator();
  const qc = useQueryClient();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["muse-asset", "list", characterId, "SNAPSHOT"],
    queryFn: () => getMuseAssetList(characterId, "SNAPSHOT"),
  });
  const items = (data?.items ?? []).filter((it) => it.status === "COMPLETED" && it.assetPath);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["muse-asset", "list", characterId, "SNAPSHOT"] });
    qc.invalidateQueries({ queryKey: ["muse-asset", "list", characterId] });
  };

  const handleSetBackground = async (item: MuseAssetItem) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      await setChatRoomBackground({ characterId, backgroundAssetId: `usergen:${item.id}` });
      qc.invalidateQueries({ queryKey: ["chat-room-background", characterId] });
      onNotice(t("museAsset.gallery.set_bg"));
    } catch {
      // 실패는 조용히 — 다음 시도 가능
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item: MuseAssetItem) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      await deleteMuseAsset(item.id);
      invalidate();
      setConfirmDeleteId(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PanelShell title={t("museAsset.gallery.title")}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: "40px" }}>
          <CircularProgress size={22} sx={{ color: HUD.cyan }} />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ py: "36px", px: "8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <Icon name="icCamera" size={30} color={HUD.dim} />
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 13, lineHeight: 1.6, color: HUD.dim, textAlign: "center" }}>
            {t("museAsset.gallery.empty")}
          </Typography>
        </Box>
      ) : (
        <Box data-scroll-container sx={{ display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto", flex: 1, minHeight: 0 }}>
          {items.map((item) => {
            const src = getAssetUrl(item.assetPath as string, item.mediaType === "VIDEO" ? undefined : "large");
            const posterSrc = item.posterPath ? getAssetUrl(item.posterPath) : null;
            return (
              <Box key={item.id} sx={{ ...PANEL, borderRadius: "14px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {/* 미디어 */}
                <Box sx={{ position: "relative", width: "100%", aspectRatio: "3/4", bgcolor: "#05060f" }}>
                  {item.mediaType === "VIDEO" ? (
                    <Box
                      component="video"
                      src={`${src}#t=0.001`}
                      poster={posterSrc ?? undefined}
                      controls
                      playsInline
                      muted
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <Box component="img" src={src} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  )}
                  {/* 커스텀 칩 */}
                  <Box sx={{ position: "absolute", top: 10, left: 10, background: "linear-gradient(100deg, #7c3aed 0%, #db2777 100%)", borderRadius: "9999px", px: "8px", py: "3px" }}>
                    <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 10.5, color: "#fff" }}>
                      {t("chat.bg_sheet.custom_badge")}
                    </Typography>
                  </Box>
                </Box>

                {/* 상세 — 남긴 순간 / 뮤즈의 감정 / 그때의 대화 */}
                <Box sx={{ px: "14px", py: "13px", display: "flex", flexDirection: "column", gap: "11px" }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: HUD.dim }}>
                    {new Date(item.createdAt).toLocaleDateString("ko-KR")}
                  </Typography>
                  {item.sceneSummary && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 11, color: HUD.cyan, letterSpacing: "0.06em" }}>
                        {t("museAsset.gallery.moment_label")}
                      </Typography>
                      <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, fontSize: 14, lineHeight: 1.55, color: HUD.text }}>
                        {item.sceneSummary}
                      </Typography>
                    </Box>
                  )}
                  {item.emotion && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 11, color: HUD.amber, letterSpacing: "0.06em" }}>
                        {t("museAsset.gallery.emotion_label")}
                      </Typography>
                      <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,224,150,0.92)" }}>
                        {item.emotion}
                      </Typography>
                    </Box>
                  )}
                  {Array.isArray(item.sourceMessages) && item.sourceMessages.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 11, color: HUD.teal, letterSpacing: "0.06em" }}>
                        {t("museAsset.gallery.source_label")}
                      </Typography>
                      <Box sx={{ borderLeft: `2px solid ${HUD.panelBorder}`, pl: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
                        {item.sourceMessages.slice(-5).map((m, i) => (
                          <Typography key={i} sx={{ ...TEMP_TEXT_SEMIBOLD_XS, fontSize: 12, lineHeight: 1.5, color: m.role === "assistant" ? "rgba(178,195,228,0.9)" : HUD.dim, wordBreak: "break-word" }}>
                            {m.content.length > 80 ? `${m.content.slice(0, 80)}…` : m.content}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* 액션 */}
                  <Box sx={{ display: "flex", gap: "8px", mt: "3px" }}>
                    <ButtonBase
                      onClick={() => handleSetBackground(item)}
                      disabled={busyId === item.id}
                      sx={{ ...PANEL, flex: 1, height: 42, borderRadius: "10px", gap: "6px", borderColor: "rgba(94,234,212,0.4)" }}
                    >
                      {busyId === item.id ? (
                        <CircularProgress size={15} sx={{ color: HUD.teal }} />
                      ) : (
                        <>
                          <Icon name="icImage" size={14} color={HUD.teal} />
                          <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: HUD.teal }}>
                            {t("museAsset.gallery.set_bg")}
                          </Typography>
                        </>
                      )}
                    </ButtonBase>
                    {confirmDeleteId === item.id ? (
                      <ButtonBase
                        onClick={() => handleDelete(item)}
                        disabled={busyId === item.id}
                        sx={{ ...PANEL, flex: 1, height: 42, borderRadius: "10px", borderColor: "rgba(229,72,77,0.55)" }}
                      >
                        <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: "#ff8d91" }}>
                          {t("museAsset.gallery.delete_confirm_title")}
                        </Typography>
                      </ButtonBase>
                    ) : (
                      <ButtonBase
                        onClick={() => setConfirmDeleteId(item.id)}
                        sx={{ ...PANEL, width: 76, height: 42, borderRadius: "10px" }}
                      >
                        <Typography sx={{ ...TEMP_TEXT_BOLD_XS, fontSize: 12.5, color: HUD.dim }}>
                          {t("museAsset.gallery.delete")}
                        </Typography>
                      </ButtonBase>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </PanelShell>
  );
}
